import { Alert, Anchor, Button, Group, PasswordInput, Stack, Text, TextInput } from '@mantine/core';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { landingPath, useAuth } from '@web/lib/auth';
import { errorMessage } from '@web/lib/errors';
import { useAcceptInvitation, useForgotPassword, useInvitationInfo, useResetPassword } from './api';
import { AuthLayout } from './AuthLayout';
import { NewPasswordField } from './NewPasswordField';

function BackToLogin() {
  const { t } = useTranslation();
  return (
    <Group justify="center" mt="md">
      <Anchor component={Link} to="/login" size="sm">
        {t('auth.backToLogin')}
      </Anchor>
    </Group>
  );
}

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const forgot = useForgotPassword();

  if (forgot.isSuccess) {
    return (
      <AuthLayout title={t('auth.forgot.sentTitle')}>
        <Alert color="green" icon={<FiCheckCircle />}>
          {t('auth.forgot.sent', { email })}
        </Alert>
        <BackToLogin />
      </AuthLayout>
    );
  }
  return (
    <AuthLayout title={t('auth.forgot.title')} subtitle={t('auth.forgot.subtitle')}>
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          forgot.mutate(email.trim());
        }}
        noValidate
      >
        <Stack gap="md">
          {forgot.isError && (
            <Alert color="red" icon={<FiAlertCircle />}>
              {errorMessage(forgot.error)}
            </Alert>
          )}
          <TextInput label={t('auth.email')} type="email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} autoComplete="username" autoFocus required />
          <Button type="submit" fullWidth loading={forgot.isPending} disabled={!/^\S+@\S+\.\S+$/.test(email.trim())}>
            {t('auth.forgot.submit')}
          </Button>
        </Stack>
      </form>
      <BackToLogin />
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [valid, setValid] = useState(false);
  const reset = useResetPassword();

  if (!token) return <Navigate to="/login" replace />;
  if (reset.isSuccess) {
    return (
      <AuthLayout title={t('auth.reset.doneTitle')}>
        <Alert color="green" icon={<FiCheckCircle />}>
          {t('auth.reset.done')}
        </Alert>
        <BackToLogin />
      </AuthLayout>
    );
  }
  return (
    <AuthLayout title={t('auth.reset.title')} subtitle={t('auth.reset.subtitle')}>
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          reset.mutate({ token, password });
        }}
        noValidate
      >
        <Stack gap="md">
          {reset.isError && (
            <Alert color="red" icon={<FiAlertCircle />}>
              {errorMessage(reset.error)}
            </Alert>
          )}
          <NewPasswordField value={password} onChange={setPassword} onCheck={(c) => setValid(c.valid)} autoFocus />
          <Button type="submit" fullWidth loading={reset.isPending} disabled={!valid}>
            {t('auth.reset.submit')}
          </Button>
        </Stack>
      </form>
      <BackToLogin />
    </AuthLayout>
  );
}

export function AcceptInvitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { status, me, signIn, reload } = useAuth();
  const info = useInvitationInfo(token || null);
  const accept = useAcceptInvitation(token);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [valid, setValid] = useState(false);

  if (!token) return <Navigate to="/login" replace />;

  if (info.isPending) {
    return (
      <AuthLayout title={t('auth.invite.title')}>
        <Text c="dimmed">{t('common.loading')}</Text>
      </AuthLayout>
    );
  }
  if (info.isError || !info.data) {
    return (
      <AuthLayout title={t('auth.invite.invalidTitle')}>
        <Alert color="red" icon={<FiAlertCircle />}>
          {t('auth.invite.invalid')}
        </Alert>
        <BackToLogin />
      </AuthLayout>
    );
  }

  const inv = info.data;
  const roleLabel = t(`roles.${inv.role}`);
  const done = (orgId: string) => navigate(`/o/${orgId}`, { replace: true });

  // An existing account accepts after signing in with the invited address.
  if (inv.existingUser) {
    const signedInAsInvitee = status === 'authenticated' && me?.user.email === inv.email;
    return (
      <AuthLayout title={t('auth.invite.title')} subtitle={t('auth.invite.subtitle', { org: inv.orgName, role: roleLabel })}>
        <Stack gap="md">
          {accept.isError && (
            <Alert color="red" icon={<FiAlertCircle />}>
              {errorMessage(accept.error)}
            </Alert>
          )}
          {signedInAsInvitee ? (
            <Button
              type="submit"
              fullWidth
              loading={accept.isPending}
              onClick={() =>
                accept.mutate(
                  {},
                  {
                    onSuccess: async (res) => {
                      await reload();
                      done(res.orgId);
                    },
                  },
                )
              }
            >
              {t('auth.invite.join', { org: inv.orgName })}
            </Button>
          ) : (
            <>
              <Alert color="blue">{t('auth.invite.signInFirst', { email: inv.email })}</Alert>
              <Button type="submit" fullWidth component={Link} to="/login" state={{ from: `/accept-invite?token=${token}` }}>
                {t('auth.login.submit')}
              </Button>
            </>
          )}
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.invite.title')} subtitle={t('auth.invite.subtitle', { org: inv.orgName, role: roleLabel })}>
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          accept.mutate(
            { name: name.trim(), password },
            {
              onSuccess: async (res) => {
                if (res.session) await signIn(res.session);
                const current = useAuth.getState().me;
                if (current && (current.user.mfaSetupRequired || current.user.mustChangePassword)) navigate(landingPath(current), { replace: true });
                else done(res.orgId);
              },
            },
          );
        }}
        noValidate
      >
        <Stack gap="md">
          {accept.isError && (
            <Alert color="red" icon={<FiAlertCircle />}>
              {errorMessage(accept.error)}
            </Alert>
          )}
          <TextInput label={t('auth.email')} value={inv.email} readOnly disabled />
          <TextInput label={t('auth.invite.name')} value={name} onChange={(e) => setName(e.currentTarget.value)} autoComplete="name" autoFocus required />
          <NewPasswordField value={password} onChange={setPassword} onCheck={(c) => setValid(c.valid)} email={inv.email} label={t('auth.password.label')} />
          <Button type="submit" fullWidth loading={accept.isPending} disabled={!valid || !name.trim()}>
            {t('auth.invite.create')}
          </Button>
        </Stack>
      </form>
    </AuthLayout>
  );
}

/** Used by the forced "change your password" step. */
export function CurrentPasswordField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation();
  return (
    <PasswordInput
      label={t('auth.password.current')}
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      autoComplete="current-password"
      required
    />
  );
}
